<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <HyperspeedBackground class="absolute inset-0 z-0" :beam-count="120" color-scheme="rainbow" :speed-factor="1.5" />
    <div
      class="max-w-md w-full bg-white/80 backdrop-blur-xl p-8 pt-10 pb-6 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10"
    >
      <ShineBorder
        :border-width="2"
        :duration="24"
        :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']"
      />

      <h1 class="text-center text-2xl font-bold text-gray-900">Sign in with password</h1>
      <p class="text-center text-sm text-gray-500 mt-2 leading-relaxed">
        For accounts that still use a password.
      </p>

      <Form @submit="login" class="mt-6 max-w-[22rem] mx-auto space-y-5" :initial-values="credentials">
        <div class="space-y-3">
          <FormField v-slot="{ componentField }" name="email">
            <FormItem>
              <FormControl>
                <Input
                  v-bind="componentField"
                  id="email"
                  type="text"
                  placeholder="Enter your email or username"
                  v-model="credentials.email"
                  required
                  class="border-[1px] border-gray-300 rounded-lg p-2.5 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-900"
                />
              </FormControl>
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="password">
            <FormItem>
              <FormControl>
                <Input
                  v-bind="componentField"
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  v-model="credentials.password"
                  required
                  class="border-[1px] border-gray-300 rounded-lg p-2.5 outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-900"
                />
              </FormControl>
            </FormItem>
          </FormField>
        </div>

        <div v-if="authStore.error" class="text-red-500 text-sm">
          {{ authStore.error }}
        </div>

        <Button
          type="submit"
          :disabled="authStore.isLoading"
          class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
        >
          <span v-if="!authStore.isLoading">Sign in</span>
          <span v-else>Signing in...</span>
        </Button>

        <div class="flex items-center justify-between text-sm">
          <router-link to="/forgot-password" class="text-indigo-600 hover:text-indigo-500">Forgot password?</router-link>
          <router-link to="/login" class="text-gray-500 hover:text-gray-700">Back to magic link</router-link>
        </div>

        <div class="text-center text-sm text-gray-600 pt-2">
          Don't have an account?
          <router-link to="/register" class="font-medium text-indigo-600 hover:text-indigo-500">
            Create account
          </router-link>
        </div>

        <p class="text-center text-xs text-gray-400 leading-relaxed">
          By continuing, you agree to our
          <router-link to="/terms-and-conditions" class="text-indigo-600 hover:text-indigo-500">Terms</router-link>
          and
          <router-link to="/privacy-policy" class="text-indigo-600 hover:text-indigo-500">Privacy Policy</router-link>
        </p>
      </Form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import HyperspeedBackground from '@/components/layout/HyperspeedBackground.vue'

interface Credentials {
  email: string
  password: string
}

const router = useRouter()
const authStore = useAuthStore()

const credentials = ref<Credentials>({ email: '', password: '' })

onMounted(async () => {
  const isAuthenticated = await authStore.checkAuth()
  if (isAuthenticated) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/')
  }
})

const login = async () => {
  try {
    await authStore.login(credentials.value)

    const redirectTo = localStorage.getItem('redirectAfterLogin')
    if (redirectTo) {
      localStorage.removeItem('redirectAfterLogin')
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push(redirectTo)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/')
    }
  } catch (error: any) {
    console.error('Login error:', error)
  }
}
</script>

<style scoped>
.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}
</style>
